import { useMemo, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import Layout from "@/components/Layout";
import { StatusBadge } from "@/components/StatusBadge";
import { Package, Loader2, Search, Wallet, Users, CircleDollarSign, TrendingUp } from "lucide-react";
import { CargusSetupBanner } from "@/components/CargusSetupBanner";

const STATE_LABEL = {
  livrat: "Livrat",
  retur: "Retur",
  livrare: "Spre destinatar",
  depozit: "În depozit",
  tranzit: "În tranzit",
  preluat: "Preluat",
  netrimis: "Netrimisă",
  anulat: "Anulat",
};

const FILTERS = [
  { id: "all", label: "Toate" },
  { id: "paid", label: "Încasate" },
  { id: "unpaid", label: "Neîncasate" },
];

function lei(n) {
  return (Number(n) || 0).toLocaleString("ro-RO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function BigStat({ icon: Icon, label, value, sub, color }) {
  return (
    <div className="border border-border rounded-sm bg-card p-4">
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</span>
        <Icon size={16} style={{ color }} />
      </div>
      <div className="mt-2 text-2xl font-mono font-semibold tracking-tight" style={{ color }}>
        {value}
      </div>
      {sub && <div className="text-[10px] text-muted-foreground font-mono mt-0.5">{sub}</div>}
    </div>
  );
}

export default function IncasariVerify() {
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    let a = result?.awbs || [];
    if (filter === "paid") a = a.filter((x) => x.paid);
    if (filter === "unpaid") a = a.filter((x) => !x.paid);
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      a = a.filter((x) => x.awb.includes(query.trim()) || (x.receiver || "").toLowerCase().includes(q));
    }
    return a;
  }, [result, filter, query]);

  const run = async () => {
    if (!fromDate || !toDate) {
      toast.error("Completează intervalul de date (de la / până la).");
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("from_date", fromDate);
      fd.append("to_date", toDate);
      const { data } = await api.post("/verify/incasari", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 0,
      });
      setResult(data);
      setFilter("all");
      toast.success(`${data.total_orders} comenzi · ${lei(data.total_incasat)} lei încasați`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  const field =
    "mt-1 w-full bg-card border border-border rounded-sm px-2.5 py-2 text-sm font-mono outline-none focus:border-primary";

  return (
    <Layout>
      <div className="p-8 max-w-[1400px] mx-auto">
        <div className="flex items-center gap-2 mb-1">
          <TrendingUp size={20} />
          <h1 className="text-3xl font-semibold tracking-tight">Încasări pe perioadă</h1>
        </div>
        <p className="text-sm text-muted-foreground mt-1 mb-8">
          Fără fișiere. Pui doar intervalul (ex. 01.09–30.09) și vezi toate comenzile cu ramburs din
          perioadă: încasate (livrate), trimise-dar-neîncasate și netrimise — cu suma și statusul fiecăreia.
          „Încasat" = colet livrat (ramburs colectat la livrare).
        </p>

        <CargusSetupBanner />

        <div className="border border-border rounded-sm bg-card p-5 mb-8 max-w-2xl">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] text-muted-foreground uppercase tracking-wider">De la data</label>
              <input
                data-testid="inc-from-date"
                type="text"
                inputMode="numeric"
                placeholder="zz.ll.aaaa"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className={field}
              />
            </div>
            <div>
              <label className="text-[10px] text-muted-foreground uppercase tracking-wider">Până la data</label>
              <input
                data-testid="inc-to-date"
                type="text"
                inputMode="numeric"
                placeholder="zz.ll.aaaa"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className={field}
              />
            </div>
          </div>
          <p className="text-[10px] text-muted-foreground font-mono mt-2 mb-4">
            Scrie liber: ex. 01.09.2026 (zz.ll.aaaa). Intervalele mari (o lună) pot dura ~60-90 sec.
          </p>

          <button
            data-testid="inc-verify-btn"
            disabled={loading}
            onClick={run}
            className="w-full flex items-center justify-center gap-2 bg-primary text-primary-foreground rounded-sm px-3 py-2.5 text-sm font-medium hover:bg-white/90 transition-colors disabled:opacity-50"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <CircleDollarSign size={16} />}
            {loading ? "Se verifică în Cargus..." : "Vezi încasările"}
          </button>
        </div>

        {result && (
          <div className="animate-fade-up">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
              <BigStat
                icon={CircleDollarSign}
                label="Total încasat (lei)"
                value={lei(result.total_incasat)}
                sub={`${result.paid_count} comenzi plătite`}
                color="#34C759"
              />
              <BigStat
                icon={Wallet}
                label="Neîncasat (lei)"
                value={lei(result.total_neincasat)}
                sub={`${result.unpaid_count} comenzi`}
                color="#FF9F0A"
              />
              <BigStat
                icon={TrendingUp}
                label="Total general (lei)"
                value={lei(result.total_general)}
                sub="ramburs total"
                color="#FFFFFF"
              />
              <BigStat icon={Package} label="Comenzi" value={result.total_orders} color="#4DA3FF" />
              <BigStat icon={Users} label="Clienți" value={result.total_clients} color="#AF7AFF" />
              <BigStat
                icon={CircleDollarSign}
                label="Plătite / Neplătite"
                value={`${result.paid_count}/${result.unpaid_count}`}
                color="#32D6C0"
              />
            </div>

            <div className="flex flex-wrap items-center gap-3 mb-4">
              <div className="flex gap-1.5">
                {FILTERS.map((f) => (
                  <button
                    key={f.id}
                    data-testid={`inc-filter-${f.id}`}
                    onClick={() => setFilter(f.id)}
                    className={`px-3 py-1.5 rounded-sm text-xs font-mono border transition-colors ${
                      filter === f.id
                        ? "bg-primary text-primary-foreground border-primary"
                        : "border-border text-muted-foreground hover:text-foreground hover:border-muted-foreground"
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
              <div className="relative ml-auto">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  data-testid="inc-search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Caută AWB sau client..."
                  className="bg-card border border-border rounded-sm pl-9 pr-3 py-1.5 text-sm font-mono outline-none focus:border-primary w-64"
                />
              </div>
            </div>

            <div className="border border-border rounded-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm font-mono">
                  <thead className="bg-[#0c0c0e] sticky top-0">
                    <tr className="text-left text-muted-foreground text-xs uppercase tracking-wider">
                      <th className="px-4 py-3 font-medium">AWB</th>
                      <th className="px-4 py-3 font-medium">Client</th>
                      <th className="px-4 py-3 font-medium">Data expediere</th>
                      <th className="px-4 py-3 font-medium">Livrare</th>
                      <th className="px-4 py-3 font-medium">Încasare</th>
                      <th className="px-4 py-3 font-medium">Data încasării</th>
                      <th className="px-4 py-3 font-medium text-right">Sumă (lei)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((a) => (
                      <tr
                        key={a.awb}
                        data-testid={`inc-row-${a.awb}`}
                        className="border-t border-border hover:bg-muted/30 transition-colors"
                      >
                        <td className="px-4 py-3 font-medium">{a.awb}</td>
                        <td className="px-4 py-3 text-muted-foreground max-w-[220px] truncate" title={a.receiver}>
                          {a.receiver || "—"}
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">{a.date || "—"}</td>
                        <td className="px-4 py-3">
                          <StatusBadge type={a.delivery_state}>
                            {STATE_LABEL[a.delivery_state] || a.state_label || a.delivery_state}
                          </StatusBadge>
                        </td>
                        <td className="px-4 py-3">
                          {a.paid ? (
                            <StatusBadge type="paid">Încasat</StatusBadge>
                          ) : (
                            <StatusBadge type="unpaid">Neîncasat</StatusBadge>
                          )}
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">{a.payment_date || "—"}</td>
                        <td className="px-4 py-3 text-right font-medium">{a.amount ? lei(a.amount) : "—"}</td>
                      </tr>
                    ))}
                    {filtered.length === 0 && (
                      <tr>
                        <td colSpan={7} className="px-4 py-10 text-center text-muted-foreground">
                          <Package size={20} className="mx-auto mb-2 opacity-50" />
                          Nicio comandă pentru acest filtru.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
